<template>
    <div class="my-2">
        <div class="flex sm:justify-between bg-minplan-beige text-minplan-blue">
            <div class="flex w-full md:w-5/8 xl:w-2/4">
                <p class="hidden md:flex w-1/4">
                    <img class="w-80 h-auto object-cover"
                         :src="project.image ? route('media.restore', {filename: project.image}) : '/images/noimg.png'" alt="" />
                </p>
                <div class="flex p-4 w-full md:w-3/4">
                    <div>
                        <LocationMarkerIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                        <button type="button" v-if="editBaseLink" @click="editBase" class="block">
                            <PencilIcon class="mr-2 mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                        </button>
                        <button type="button" v-if="editImageLink" @click="editImage" class="block">
                            <PhotographIcon class="mr-2 mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                        </button>
                    </div>
                    <div>
                        <span class="truncate inline-block w-full font-bold">{{ $t('projects.project') }} {{ project.name }}</span><br>
                        <span class="">{{ project.street }}, {{ project.zip }} {{ project.city }}</span><br>
                        <p class="mt-2 text-minplan-darkbeige text-sm">{{ $t('projects.projectNumber') }} {{ project.number }}</p>
                        <p  class="mt-2 text-minplan-darkbeige text-xs" v-if="editBaseLink">
                            {{ $t('projects.baseFee') }} {{ project.base_fee }}% / {{ $t('projects.consultancyFee') }} {{ project.consultancy_fee }}%
                        </p>
                        <!-- Auftrag Luca, 2026-08-20, Entscheid 12: der
                             Berechnungsmodus steht dort, wo Gebuehr und
                             Honorar stehen — er erklaert, worauf die beiden
                             Saetze angewandt werden. Zustand und Dialog liegen
                             in der Kindkomponente; dieser Header bleibt
                             zustandslos. -->
                        <p class="mt-1 text-minplan-darkbeige text-xs" v-if="feeCalculation">
                            <project-fee-calculation-mode
                                :project="project"
                                :fee-calculation="feeCalculation"
                            />
                        </p>
                    </div>
                </div>
            </div>
            <div class="hidden md:flex flex-col p-4 md:w-3/8 xl:w-1/4 space-y-3">
                <div class="flex items-start">
                    <div class="flex flex-col items-center mr-2">
                        <OfficeBuildingIcon class="h-5 w-5 text-minplan-darkgreybeige flex-shrink-0" />
                        <button type="button" v-if="editContactLink" @click="editContact" class="mt-2 text-minplan-darkbeige hover:text-minplan-darkgreybeige">
                            <PencilIcon class="mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                        </button>
                    </div>
                    <div class="min-w-0 flex-1">
                        <span class="truncate inline-block font-bold w-full">{{ $t('projects.projectLead') }}</span>
                        <span class="block truncate mt-1" :title="project.contact_company">{{ project.contact_company }}</span>
                        <span class="block truncate text-minplan-darkbeige text-sm leading-5 mt-1" :title="project.contact_firstname + ' ' + project.contact_lastname">{{ project.contact_firstname }} {{ project.contact_lastname }}</span>
                    </div>
                </div>
                <div v-if="project.contact_email" class="flex items-center min-w-0">
                    <a :href="'mailto:' + project.contact_email + '?subject=' + $t('projects.project') + ' ' + project.name" class="flex items-center min-w-0 hover:text-minplan-blue text-minplan-darkbeige text-sm leading-5 w-full" :title="project.contact_email">
                        <MailIcon class="mr-2 h-5 w-5 flex-shrink-0" />
                        <span class="truncate">{{ project.contact_email }}</span>
                    </a>
                </div>
                <div v-if="project.contact_mobile" class="flex items-center min-w-0">
                    <a :href="'tel:' + (project.contact_mobile ? project.contact_mobile.replace(/[^0-9]/g, '') : '')" class="flex items-center min-w-0 hover:text-minplan-blue text-minplan-darkbeige text-sm leading-5 w-full" :title="project.contact_mobile">
                        <DeviceMobileIcon class="mr-2 h-5 w-5 flex-shrink-0" />
                        <span class="truncate">{{ project.contact_mobile }}</span>
                    </a>
                </div>
            </div>
            <div class="hidden xl:flex flex-col p-4 w-1/4 space-y-3">
                <div class="flex items-start">
                    <div class="flex flex-col items-center mr-2">
                        <UserIcon class="h-5 w-5 text-minplan-blue flex-shrink-0" />
                        <button type="button" v-if="editResponsibleLink" @click="editResponsible" class="mt-2 text-minplan-darkbeige hover:text-minplan-darkgreybeige">
                            <PencilIcon class="mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                        </button>
                    </div>
                    <div class="min-w-0 flex-1">
                        <span class="truncate inline-block font-bold w-full">
                            {{ $t('projects.projectAssistance') }}
                        </span>
                        <span class="block truncate mt-1" :title="project.responsible">{{ project.responsible }}</span>
                        <span class="block truncate text-minplan-darkbeige text-sm mt-1" :title="project.agency">{{ project.agency }}</span>
                    </div>
                </div>
                <div v-if="project.responsible_email"  class="flex items-center min-w-0">
                    <a :href="'mailto:' + project.responsible_email + '?subject=' + $t('projects.project') + ' ' + project.name" class="flex items-center min-w-0 hover:text-minplan-blue text-minplan-darkbeige text-sm w-full" :title="project.responsible_email">
                        <MailIcon class="mr-2 h-5 w-5 flex-shrink-0" />
                        <span class="truncate">{{ project.responsible_email }}</span>
                    </a>
                </div>
                <div v-if="project.responsible_mobile" class="flex items-center min-w-0">
                    <a :href="'tel:' + (project.responsible_mobile ? project.responsible_mobile.replace(/[^0-9]/g, '') : '')" class="flex items-center min-w-0 hover:text-minplan-blue text-minplan-darkbeige text-sm w-full" :title="project.responsible_mobile">
                        <DeviceMobileIcon class="mr-2 h-5 w-5 flex-shrink-0" />
                        <span class="truncate">{{ project.responsible_mobile }}</span>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { LocationMarkerIcon, UserIcon, PencilIcon, PhotographIcon, OfficeBuildingIcon, MailIcon, DeviceMobileIcon } from "@heroicons/vue/outline";
import ProjectFeeCalculationMode from "@/Shared/Components/Projects/ProjectFeeCalculationMode";


export default {
    props: [
        "project",
        "editBaseLink",
        "editImageLink",
        "editContactLink",
        "editResponsibleLink",
        // Auftrag Luca, 2026-08-20. Optional gehalten (Fix-Runde nach dem
        // code-reviewer): heute bindet nur `Pages/Projects/Show.vue` diesen
        // Header ein und reicht den Block immer mit. Das `v-if` ist Vorsorge
        // fuer einen zweiten Aufrufer ohne Modus — nicht die Beschreibung
        // eines bestehenden.
        "feeCalculation",
    ],
    components: {
        ProjectFeeCalculationMode,
        PencilIcon,
        LocationMarkerIcon,
        UserIcon,
        PhotographIcon,
        OfficeBuildingIcon,
        MailIcon,
        DeviceMobileIcon
    },
    methods: {
        editBase(){
            this.$inertia.visitInModal(this.editBaseLink)
        },
        editImage(){
            this.$inertia.visitInModal(this.editImageLink)
        },
        editContact(){
            this.$inertia.visitInModal(this.editContactLink)
        },
        editResponsible(){
            this.$inertia.visitInModal(this.editResponsibleLink)
        },
    }
};
</script>

<style scoped>

</style>
